import React from 'react';
import Icon from '../shared/Icon.tsx';
import {
    AtSignIcon,
    PaintbrushIcon,
    ReplyIcon,
    SendIcon,
    UserPlusIcon,
    UsersIcon
} from 'lucide-react';
import {Link, useLocation} from 'react-router-dom';
import {NotificationModel, NotificationType} from '../../api/types.ts';
import projectUrl from '../../lib/projectUrl.ts';
import fromLocationState from '../../lib/fromLocationState.ts';
import {GroupPageTabNames} from '../groups/GroupPage.tsx';
import EmojiImage from '../../emojis/EmojiImage.tsx';
import displayName from '../../lib/displayName.ts';

interface MxNotificationCardProps {
    notification: NotificationModel;
}

const MxNotificationCard: React.FC<MxNotificationCardProps> = ({
    notification
}) => {
    const location = useLocation();

    const notificationImageSrc = () => {
        if (notification.notificationType === NotificationType.ProjectAdded) {
            return notification.mix.thumbnail;
        }

        if (notification.notificationType === NotificationType.Invite) {
            return notification.collectionThumbnail;
        }

        return notification.initiatedBy.thumbnail;
    };

    const notificationImageAlt = () => {
        if (notification.notificationType === NotificationType.ProjectAdded) {
            return 'Mix thumbnail';
        }

        if (notification.notificationType === NotificationType.Invite) {
            return 'Group thumbnail';
        }

        return `@${notification.initiatedBy.nickname} profile image`;
    };

    const notificationUrl = () => {
        if (notification.notificationType === NotificationType.ProjectAdded) {
            return projectUrl(notification.mix);
        }

        if (notification.notificationType === NotificationType.Invite) {
            return '/explore/groups';
        }

        if (notification.notificationType === NotificationType.RequestToJoin) {
            return `/explore/groups/${notification.collectionContainerName}?tab=${GroupPageTabNames.Members}`;
        }

        const channelName = encodeURIComponent(notification.channelName);
        const postId = notification.postId ?? '';
        const parentPostId = notification.parentPostId ?? '';

        return `/explore/groups/${notification.collectionContainerName}?tab=${GroupPageTabNames.Posts}&ch=${channelName}&pid=${postId}&ppid=${parentPostId}`;
    };

    return (
        <Link
            className={`notification-card${notification.viewed ? '' : ' new'}`}
            to={notificationUrl()}
            state={fromLocationState(location).state}
        >
            <div className="notification-img-holder">
                <img
                    src={notificationImageSrc()}
                    crossOrigin="anonymous"
                    alt={notificationImageAlt()}
                />
                <span className="notification-type">
                    {notification.notificationType ===
                        NotificationType.ProjectAdded && (
                        <Icon icon={PaintbrushIcon} />
                    )}
                    {notification.notificationType ===
                        NotificationType.Mention && <Icon icon={AtSignIcon} />}
                    {notification.notificationType ===
                        NotificationType.NewPost && <Icon icon={SendIcon} />}
                    {notification.notificationType ===
                        NotificationType.PostReaction && (
                        <EmojiImage emojiCode={notification.data} />
                    )}
                    {notification.notificationType ===
                        NotificationType.PostReply && <Icon icon={ReplyIcon} />}
                    {notification.notificationType ===
                        NotificationType.Invite && <Icon icon={UsersIcon} />}
                    {notification.notificationType ===
                        NotificationType.RequestToJoin && (
                        <Icon icon={UserPlusIcon} />
                    )}
                </span>
            </div>
            <div className="notification-info">
                {notification.notificationType ===
                    NotificationType.ProjectAdded && (
                    <span>
                        <span className="fw-bold">
                            {displayName(notification.initiatedBy)}
                        </span>{' '}
                        added a new project{' '}
                        <span className="fw-bold">{notification.mix.name}</span>
                    </span>
                )}
                {notification.notificationType === NotificationType.Mention && (
                    <span>
                        <span className="fw-bold">
                            {displayName(notification.initiatedBy)}
                        </span>{' '}
                        mentioned you in a post in{' '}
                        <span className="fw-bold">
                            {notification.collectionName} #
                            {notification.channelName}
                        </span>
                    </span>
                )}
                {notification.notificationType === NotificationType.NewPost && (
                    <span>
                        <span className="fw-bold">
                            {displayName(notification.initiatedBy)}
                        </span>{' '}
                        added a new post in{' '}
                        <span className="fw-bold">
                            {notification.collectionName} #
                            {notification.channelName}
                        </span>
                    </span>
                )}
                {notification.notificationType ===
                    NotificationType.PostReaction && (
                    <span>
                        <span className="fw-bold">
                            {displayName(notification.initiatedBy)}
                        </span>{' '}
                        reacted to your post in{' '}
                        <span className="fw-bold">
                            {notification.collectionName} #
                            {notification.channelName}
                        </span>
                    </span>
                )}
                {notification.notificationType ===
                    NotificationType.PostReply && (
                    <span>
                        <span className="fw-bold">
                            {displayName(notification.initiatedBy)}
                        </span>{' '}
                        replied to your post in{' '}
                        <span className="fw-bold">
                            {notification.collectionName} #
                            {notification.channelName}
                        </span>
                    </span>
                )}
                {notification.notificationType === NotificationType.Invite && (
                    <span>
                        <span className="fw-bold">
                            {displayName(notification.initiatedBy)}
                        </span>{' '}
                        invited you to join{' '}
                        <span className="fw-bold">
                            {notification.collectionName}
                        </span>
                    </span>
                )}
                {notification.notificationType ===
                    NotificationType.RequestToJoin && (
                    <span>
                        <span className="fw-bold">
                            {displayName(notification.initiatedBy)}
                        </span>{' '}
                        requested to join{' '}
                        <span className="fw-bold">
                            {notification.collectionName}
                        </span>
                    </span>
                )}
            </div>
        </Link>
    );
};

export default MxNotificationCard;
